// updateLanguage.js
'use strict';
import { replace as historyReplace } from 'redux-first-history';
import { MERGE_OPERATION_DATA } from '../ActionTypes.js';
import removeShopCurrency from '../action/removeShopCurrency.js';
import i18n, { localeConvertor } from '../resource/i18n.js';
import { setRemoteMeta } from '../resource/mixpanel.js';
import { getSupportedLanguage } from '../resource/getCurrentLanguage.js';
import { removeLangFromPath, generateLangPath } from '../resource/langPath.js';
import getMeData from '../selector/getMeData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getOperationData from '../selector/getOperationData.js';
import getRouterData from '../selector/getRouterData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import * as MessageTypes from '../serviceWorker/AppMessageTypes.js';
import { sendMessageToSW } from '../serviceWorker/helpers.js';
import postMessageToApp from '../action/postMessageToApp.js';

/**
 * Update language (both i18n.language and redux)
 * @kind action
 * @param {string} {language} - action param.
 * @return {Promise} Action promise.
 */
const updateLanguage =
  ({ language }) =>
  async (dispatch, getState) => {
    const lang = getSupportedLanguage({
      language,
      supportedLanguages: getRemoteConfigData(getState(), SUPPORTED_LANGUAGES),
    });

    i18n.changeLanguage(lang, error => {
      if (error) {
        return;
      }

      const pathname = getRouterData(getState(), 'location.pathname');
      const currentPath = removeLangFromPath(pathname);
      const newPath = generateLangPath(currentPath, lang);

      if (pathname !== newPath) {
        const search = getRouterData(getState(), 'location.search') || '';
        const hash = getRouterData(getState(), 'location.hash') || '';
        const state = getRouterData(getState(), 'location.state');
        dispatch(historyReplace(newPath + search + hash, state));
      }

      setRemoteMeta({ data: {} });
      dispatch(
        postMessageToApp({
          message: 'onLanguageTagUpdate',
          messageArgs: [lang],
        })
      );
    });

    const usingShopId = getOperationData(getState(), ['shop'], 'usingShopId');
    // Don't remove currency if have `usingShopId`.
    // Remove currency break product detail UI, since there are some value need currency.
    if (!usingShopId) {
      dispatch(removeShopCurrency());
    }

    sendMessageToSW({
      type: MessageTypes.SET_AUTHORIZER_LANGUAGE,
      payload: {
        language: localeConvertor({ locale: language, isISO639: true }),
      },
    });
    // re-subscribe presence-enc-client@{CLIENT_ID}
    const clientId = getMeData(getState(), 'clientId');
    sendMessageToSW({
      type: MessageTypes.SUBSCRIBE_PRESENCE_CLIENT_CHANNEL,
      payload: {
        clientId,
      },
    });
    // re-subscribe presence-enc-user@{USER_ID}
    const meId = getMeData(getState(), 'id');
    sendMessageToSW({
      type: MessageTypes.SUBSCRIBE_PRESENCE_USER_CHANNEL,
      payload: {
        userId: meId,
      },
    });
    return dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['config'],
        data: {
          language: lang,
        },
      },
    });
  };

export default updateLanguage;
